// Recovery for stale / broken lazy-chunk imports.
//
// After a deploy, an already-open page still references the previous build's
// hashed chunk names. Navigating to a lazy route then fails to import them:
// either the modulepreload 404s (fires `vite:preloadError`), or the stale chunk
// falls through to the SSR HTML shell (served 200 as text/html) and the dynamic
// import() rejects with an "Importing a module script failed" / wrong-MIME
// error. React.lazy re-throws that during render, where an <ErrorBoundary>
// catches it — so the global window handlers never see it. Both paths funnel
// through here so the fix (reload once to pull fresh assets) lives in one place.

/** Whether an error message looks like a failed dynamic-import / stale chunk. */
export const looksLikeChunkLoadError = (msg: string): boolean =>
  /importing a module script failed/i.test(msg) ||
  /failed to fetch dynamically imported module/i.test(msg) ||
  /error loading dynamically imported module/i.test(msg) ||
  /not a valid javascript mime type/i.test(msg) ||
  /'text\/html' is not a valid/i.test(msg) ||
  /loading chunk [\w-]+ failed/i.test(msg) ||
  /chunkloaderror/i.test(msg);

const KEY = "ph_chunk_reloaded_at";
// Module-scope fallback for when sessionStorage is unavailable (private mode /
// partitioned storage). Resets on reload, so the sessionStorage guard is the
// real loop-breaker; this just avoids throwing.
let reloadedThisSession = false;

/**
 * Reload the page once to pull fresh assets, guarded so a genuinely broken
 * chunk can't loop forever (at most one reload per ~10s). Returns true if a
 * reload was triggered.
 */
export const reloadOnceForChunkError = (): boolean => {
  if (typeof window === "undefined") return false;
  const now = Date.now();
  let last = 0;
  try {
    last = Number(sessionStorage.getItem(KEY) || 0);
  } catch {
    last = reloadedThisSession ? now : 0;
  }
  if (now - last > 10_000) {
    reloadedThisSession = true;
    try {
      sessionStorage.setItem(KEY, String(now));
    } catch {
      /* storage blocked — rely on the module flag for this session */
    }
    window.location.reload();
    return true;
  }
  return false;
};
